import type { PosCatalog, PosProduct } from "../api/catalog-types";
import "./cashier-presentation-v2-surfaces.css";
import "../onboarding/cashier-empty-state.css";

type Language = "ar" | "en";

type CashierCatalogV2Props = {
  language: Language;
  catalog: PosCatalog | null;
  products: PosProduct[];
  selectedCategoryId: string;
  productSearch: string;
  disabled?: boolean;
  formatMoney: (minor: number, currency?: string) => string;
  onSearchChange: (value: string) => void;
  onSelectCategory: (categoryId: string) => void;
  onOpenProduct: (product: PosProduct) => void;
};

/** Catalog presentation owned by V2. No business mutation is implemented here. */
export function CashierCatalogV2({
  language,
  catalog,
  products,
  selectedCategoryId,
  productSearch,
  disabled = false,
  formatMoney,
  onSearchChange,
  onSelectCategory,
  onOpenProduct
}: CashierCatalogV2Props) {
  const ar = language === "ar";
  const emptyCatalog = Boolean(catalog && catalog.products.length === 0);
  const currencyPending = catalog?.currency === "XXX";
  const firstLoginSetupAvailable = import.meta.env.VITE_APP_MODE !== "cashier";

  const openFirstLoginSetup = () => {
    window.dispatchEvent(new Event("leylion:first-login-open"));
  };

  return (
    <div className={`cashier-v2-catalog${disabled ? " is-disabled" : ""}`}>
      <label className="cashier-v2-catalog__search">
        <span aria-hidden="true">⌕</span>
        <input
          type="search"
          value={productSearch}
          disabled={disabled || emptyCatalog}
          placeholder={ar ? "ابحث عن منتج أو امسح الباركود" : "Search products or scan barcode"}
          aria-label={ar ? "بحث المنتجات والباركود" : "Product and barcode search"}
          onChange={(event) => onSearchChange(event.target.value)}
        />
        {productSearch && !disabled && !emptyCatalog ? (
          <button type="button" onClick={() => onSearchChange("")} aria-label={ar ? "مسح البحث" : "Clear search"}>×</button>
        ) : null}
      </label>

      <div className="cashier-v2-catalog__categories" role="tablist" aria-label={ar ? "تصنيفات المنتجات" : "Product categories"}>
        <button
          type="button"
          role="tab"
          aria-selected={selectedCategoryId === ""}
          className={selectedCategoryId === "" ? "is-active" : ""}
          disabled={disabled || emptyCatalog}
          onClick={() => onSelectCategory("")}
        >
          {ar ? "الكل" : "All"}
        </button>
        {catalog?.categories.map((category) => (
          <button
            key={category.id}
            type="button"
            role="tab"
            aria-selected={selectedCategoryId === category.id}
            className={selectedCategoryId === category.id ? "is-active" : ""}
            disabled={disabled}
            onClick={() => onSelectCategory(category.id)}
          >
            {ar ? category.nameAr : category.nameEn}
          </button>
        ))}
      </div>

      {emptyCatalog ? (
        <div className="cashier-v2-catalog__empty" data-testid="cashier-zero-data-catalog">
          <div className="cashier-v2-catalog__empty-inner">
            <span className="cashier-v2-catalog__empty-icon" aria-hidden="true">＋</span>
            <h3>
              {currencyPending
                ? ar ? "اختر العملة الأساسية للبدء" : "Choose your base currency to begin"
                : ar ? "أضف أول منتج وابدأ البيع" : "Add your first product and start selling"}
            </h3>
            <p>
              {currencyPending
                ? ar
                  ? "مساحة العمل جاهزة وكتالوجك فارغ. اعتمد العملة أولًا حتى تُحفظ الأسعار والمبيعات والتقارير بصورة صحيحة."
                  : "Your workspace is ready and the catalog is empty. Confirm currency first so prices, sales and reports remain correct."
                : ar
                  ? "لا توجد منتجات تجريبية في حسابك. أضف منتجاتك يدويًا أو استورد ملف CSV من إعداد البداية."
                  : "Your account contains no demo products. Add your own items or import a CSV file from first-login setup."}
            </p>
            {firstLoginSetupAvailable ? (
              <div className="cashier-v2-catalog__empty-actions">
                <button type="button" onClick={openFirstLoginSetup}>
                  {currencyPending
                    ? ar ? "اختيار العملة" : "Choose currency"
                    : ar ? "إضافة أو استيراد المنتجات" : "Add or import products"}
                </button>
              </div>
            ) : (
              <p className="cashier-v2-catalog__empty-hint">
                {ar
                  ? "أكمل العملة والمنتجات من تطبيق المالك، ثم حدّث شاشة الكاشير."
                  : "Complete currency and products in the owner application, then refresh Cashier."}
              </p>
            )}
            <p className="cashier-v2-catalog__empty-hint">
              {ar
                ? "يمكنك إكمال ساعات العمل والفريق والأجهزة لاحقًا من داخل Leylion."
                : "Business hours, team and devices can be completed later from inside Leylion."}
            </p>
          </div>
        </div>
      ) : (
        <div className="cashier-product-grid cashier-v2-catalog__grid" data-testid="pos-product-grid">
          {products.map((product) => {
            const variant = product.variants.find((item) => item.isDefault) ?? product.variants[0];
            return (
              <button
                key={product.id}
                type="button"
                className="cashier-v2-product"
                data-testid={`pos-product-${product.code}`}
                disabled={disabled}
                onClick={() => onOpenProduct(product)}
              >
                {product.imageUrl ? (
                  <span className="cashier-v2-product__media">
                    <img src={product.imageUrl} alt="" loading="lazy" decoding="async" />
                  </span>
                ) : null}
                <span className="cashier-v2-product__copy">
                  <strong>{ar ? product.nameAr : product.nameEn}</strong>
                </span>
                <span className="cashier-v2-product__footer">
                  <span className="cashier-v2-product__price-block">
                    <strong>{formatMoney(variant?.unitPriceMinor ?? 0, catalog?.currency)}</strong>
                    <small>{product.code}</small>
                  </span>
                  <span className="cashier-v2-product__add" aria-hidden="true">＋</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
